import { Icon } from '@beacon/ui/components';

const ACCENT = 'var(--color-ds-orange-9)';
const BORDER = 'var(--color-ds-stroke-primary)';
const MUTED = 'var(--color-ds-surface-2)';
const SURFACE = 'var(--color-ds-surface-foundation)';

// a single grey placeholder line (fake text / fake data inside the previews)
function SkeletonLine({
  width,
  height = 5,
  color = MUTED,
  radius = 2,
  style,
}: {
  width: number | string;
  height?: number;
  color?: string;
  radius?: number;
  style?: React.CSSProperties;
}) {
  return <span style={{ display: 'block', width, height, borderRadius: radius, background: color, ...style }} />;
}

// the dotted grid canvas each preview is centered inside
function PreviewFrame({ children, height = 128 }: { children: React.ReactNode; height?: number }) {
  return (
    <div
      className="border-ds-stroke-primary relative overflow-hidden border"
      style={{
        height,
        background: 'var(--color-ds-surface-1)',
        backgroundImage: 'radial-gradient(circle, var(--color-ds-neutral-a5) 1px, transparent 1px)',
        backgroundSize: '12px 12px',
      }}
    >
      <div className="absolute inset-0 flex items-center justify-center p-3.5">{children}</div>
    </div>
  );
}

// describe the role - conversation feel
function DescribeRolePreview() {
  return (
    <PreviewFrame>
      <div className="flex w-full flex-col gap-1.5" style={{ maxWidth: 210 }}>
        <div
          className="self-start border px-2 py-1.5"
          style={{ borderColor: BORDER, background: SURFACE, width: '72%' }}
        >
          <SkeletonLine width="100%" />
          <SkeletonLine width="62%" style={{ marginTop: 4 }} />
        </div>
        <div className="self-end px-2 py-1.5" style={{ background: ACCENT, opacity: 0.85, width: '58%' }}>
          <SkeletonLine width="100%" color="rgba(0,0,0,.32)" />
          <SkeletonLine width="44%" color="rgba(0,0,0,.32)" style={{ marginTop: 4 }} />
        </div>
        <div
          className="self-start border px-2 py-1.5"
          style={{ borderColor: BORDER, background: SURFACE, width: '66%' }}
        >
          <SkeletonLine width="100%" />
          <SkeletonLine width="80%" style={{ marginTop: 4 }} />
          <SkeletonLine width="35%" style={{ marginTop: 4 }} />
        </div>
      </div>
    </PreviewFrame>
  );
}

// publish: a draft hiring criteria becomes the live V1
function PublishPreview() {
  const card = (label: string, isLive: boolean) => (
    <div
      className="border px-2.5 py-2"
      style={{
        borderColor: isLive ? ACCENT : BORDER,
        background: SURFACE,
        width: 92,
        borderTop: isLive ? `2px solid ${ACCENT}` : `1px solid ${BORDER}`,
      }}
    >
      <span
        className="text-3xs font-mono tracking-wider uppercase"
        style={{ color: isLive ? 'var(--color-ds-text-brand)' : 'var(--color-ds-text-tertiary)' }}
      >
        {label}
      </span>
      <SkeletonLine width="100%" style={{ marginTop: 6 }} />
      <SkeletonLine width="70%" style={{ marginTop: 4 }} />
      <SkeletonLine width="84%" style={{ marginTop: 4 }} />
    </div>
  );

  return (
    <PreviewFrame>
      <div className="flex items-center gap-3">
        {card('Draft', false)}
        <Icon symbol="arrow-right-line" className="text-ds-icon-brand" />
        {card('V1 · Live', true)}
      </div>
    </PreviewFrame>
  );
}

// add candidates: multiple ways into a talent pool — the extension (primary) and CSV import/manual
function AddCandidatesPreview() {
  const source = (symbol: 'linkedin-box-fill' | 'file-text-line', label: string, primary: boolean) => (
    <div
      className={
        primary
          ? 'border-ds-orange-9 text-ds-text-brand bg-ds-surface-foundation flex items-center gap-1.5 border border-t-2 px-2 py-1.5'
          : 'border-ds-stroke-primary text-ds-text-tertiary bg-ds-surface-foundation flex items-center gap-1.5 border px-2 py-1.5'
      }
    >
      <Icon symbol={symbol} className="size-3 flex-none" />
      <span className="text-3xs truncate font-mono">{label}</span>
    </div>
  );

  return (
    <PreviewFrame>
      <div className="flex w-full max-w-[236px] items-center gap-2">
        <div className="flex flex-1 flex-col gap-1.5">
          {source('linkedin-box-fill', 'From LinkedIn', true)}
          {source('file-text-line', 'candidates.csv', false)}
        </div>
        <Icon symbol="arrow-right-line" className="text-ds-icon-brand size-3 flex-none" />
        <div className="border-ds-stroke-primary bg-ds-surface-foundation flex-1 border">
          <div className="text-3xs text-ds-text-tertiary border-ds-stroke-primary border-b px-2 py-1 font-mono tracking-wider uppercase">
            Talent pool
          </div>
          <div className="flex flex-col gap-1 p-1.5">
            {[0, 1, 2].map((cardIndex) => (
              <div
                key={cardIndex}
                className={
                  cardIndex === 0
                    ? 'border-ds-stroke-primary bg-ds-surface-2 border px-1.5 py-0.5'
                    : 'border-ds-stroke-primary border px-1.5 py-0.5'
                }
              >
                <SkeletonLine width="70%" height={4} />
                <SkeletonLine width="45%" height={3} style={{ marginTop: 2 }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </PreviewFrame>
  );
}

// score: rows filling in with recommendations as scoring progresses
function ScorePreview() {
  const rows = [
    { scored: true, dotColor: 'var(--color-ds-green-9)', barWidth: 34 },
    { scored: true, dotColor: 'var(--color-ds-green-9)', barWidth: 34 },
    { scored: true, dotColor: ACCENT, barWidth: 44 },
    { scored: false, dotColor: '', barWidth: 0 },
    { scored: false, dotColor: '', barWidth: 0 },
  ];

  return (
    <PreviewFrame>
      <div className="w-full" style={{ maxWidth: 228 }}>
        <div className="mb-2 flex items-center gap-2">
          <div className="h-1 flex-1 overflow-hidden rounded-full" style={{ background: MUTED }}>
            <div className="h-full rounded-full" style={{ width: '34%', background: ACCENT }} />
          </div>
          <span className="text-ds-text-tertiary text-3xs font-mono tabular-nums">136 / 400</span>
        </div>
        <div className="border" style={{ borderColor: BORDER, background: SURFACE }}>
          {rows.map((row, rowIndex) => (
            <div key={rowIndex} className="flex items-center gap-2 border-b px-2 py-1" style={{ borderColor: BORDER }}>
              <SkeletonLine width={34} height={4} />
              <div className="ml-auto flex items-center gap-1.5">
                {row.scored ? (
                  <>
                    <span style={{ width: 5, height: 5, borderRadius: 99, background: row.dotColor }} />
                    <SkeletonLine width={row.barWidth} height={4} color="var(--color-ds-neutral-a6)" />
                  </>
                ) : (
                  <SkeletonLine width={40} height={4} color="var(--color-ds-neutral-a3)" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </PreviewFrame>
  );
}

// pipeline: rows moving from talent pool table to pipeline table
function PipelinePreview() {
  const column = (label: string, cardCount: number, active: boolean) => (
    <div className="flex-1 border" style={{ borderColor: active ? ACCENT : BORDER, background: SURFACE }}>
      <div
        className="text-3xs border-b px-2 py-1 font-mono tracking-wider uppercase"
        style={{
          borderColor: BORDER,
          color: active ? 'var(--color-ds-text-brand)' : 'var(--color-ds-text-tertiary)',
        }}
      >
        {label}
      </div>
      <div className="flex flex-col gap-1 p-1.5">
        {Array.from({ length: cardCount }).map((_, cardIndex) => (
          <div
            key={cardIndex}
            className="border px-1.5 py-0.5"
            style={{
              borderColor: BORDER,
              background: active && cardIndex === 0 ? MUTED : 'transparent',
            }}
          >
            <SkeletonLine width="70%" height={4} />
            <SkeletonLine width="45%" height={3} style={{ marginTop: 2 }} />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <PreviewFrame>
      <div className="flex w-full items-start gap-2" style={{ maxWidth: 236 }}>
        {column('Talent pool', 3, false)}
        <Icon
          symbol="arrow-right-line"
          className="text-ds-icon-brand mt-5 flex-none"
          style={{ width: 12, height: 12 }}
        />
        {column('Pipeline', 2, true)}
      </div>
    </PreviewFrame>
  );
}

interface OnboardingStep {
  number: string;
  title: string;
  Preview: () => React.ReactElement;
}

const STEPS: OnboardingStep[] = [
  {
    number: '01',
    title: 'Define what right looks like for this role',
    Preview: DescribeRolePreview,
  },
  {
    number: '02',
    title: 'Review, Calibrate, & Publish the Criteria',
    Preview: PublishPreview,
  },
  {
    number: '03',
    title: 'Add Candidates from LinkedIn or Import a List',
    Preview: AddCandidatesPreview,
  },
  {
    number: '04',
    title: 'Beacon Generates Evidence-Based Scoring',
    Preview: ScorePreview,
  },
  {
    number: '05',
    title: 'Pipeline & Progress Your Top Choices',
    Preview: PipelinePreview,
  },
];

export const onboardingIntro = (
  <div className="px-4 pt-5 pb-4 sm:px-7 sm:pt-6 sm:pb-5">
    <span className="text-ds-text-brand text-2xs font-mono">// Welcome to Beacon</span>
    <h2 className="text-ds-text-primary text-display mt-2 font-bold tracking-tight" style={{ maxWidth: 420 }}>
      In about 15 minutes you'll have crystal clear recommendations on who meets or exceeds the hiring bar that YOU
      define.
    </h2>
    <p className="text-ds-text-secondary text-md mt-2.5 leading-relaxed" style={{ textWrap: 'pretty' }}>
      Building and scaling a high-performing team requires a clear hiring bar for each role. Beacon guides you through
      defining that hiring bar, measuring candidate evidence against that bar, and consistently progressing and hiring
      top performers. Beacon helps you proactively avoid decisions based on gut, inconsistency, or bias.
    </p>
  </div>
);

export const onboardingBuild = (
  <div className="flex flex-col" style={{ maxHeight: 'calc(100vh - 180px)' }}>
    <div className="px-4 pt-5 pb-3 sm:px-7 sm:pt-6">
      <span className="text-ds-text-brand text-2xs font-mono">// The walkthrough</span>
      <h2 className="text-ds-text-primary text-display mt-2 font-bold tracking-tight">Build your TalentOS</h2>
    </div>
    <div className="grid grid-cols-1 gap-x-3.5 gap-y-4 overflow-y-auto px-4 pt-1 pb-5 sm:grid-cols-3 sm:px-7">
      {STEPS.map((step, index) => (
        <div key={step.number}>
          <div style={{ minHeight: '3.4em' }}>
            <span className="text-ds-text-brand text-3xs block font-mono tracking-wider uppercase">
              Step {index + 1}
            </span>
            <span
              className="text-ds-text-primary text-md block font-bold tracking-tight"
              style={{ lineHeight: 1.3, textWrap: 'pretty' }}
            >
              {step.title}
            </span>
          </div>
          <step.Preview />
        </div>
      ))}
    </div>
  </div>
);
